import { PipelineFacets } from './AmbientFacets';

const stages = [
  ["01", "Source task"],
  ["02", "Invariant problem kernel"],
  ["03", "Structured task profile"],
  ["04", "Target reasoning requirement"],
  ["05", "Generation + validation"],
  ["06", "Accepted training example"],
];

const positions = [
  [82, 190],
  [265, 108],
  [451, 190],
  [637, 108],
  [823, 190],
  [1009, 108],
];

const routeSegments = positions.slice(0, -1).map(([x, y], index) => {
  const [nextX, nextY] = positions[index + 1];
  return {
    x1: x + 142,
    y1: y + 36,
    x2: nextX - 14,
    y2: nextY + 36,
  };
});

const animationDuration = "6s";

function signalTiming(index: number) {
  const start = 0.02 + index * 0.18;
  const end = start + 0.18;
  const fade = 0.018;

  return {
    movement: `0;${start.toFixed(3)};${end.toFixed(3)};1`,
    opacity: `0;${start.toFixed(3)};${(start + fade).toFixed(3)};${(end - fade).toFixed(3)};${end.toFixed(3)};1`,
  };
}

export function PipelineGraphic() {
  return (
    <div className="pipeline-visual reveal">
      <svg
        className="pipeline-svg"
        viewBox="0 0 1200 470"
        role="img"
        aria-label="MAVERICK pipeline: source task, invariant problem kernel, structured task profile with multi-axis metadata, target reasoning requirement, controlled generation and validation, then an accepted training example for student training. Rejected examples branch out before training."
      >
        <defs>
          <marker id="pipeline-arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <path d="M0 0 8 4 0 8Z" fill="#cdd3d6" />
          </marker>
        </defs>

        <PipelineFacets />
        <g className="pipeline-route" fill="none" markerEnd="url(#pipeline-arrow)">
          {positions.slice(0, -1).map(([x, y], index) => {
            const [nextX, nextY] = positions[index + 1];
            return <line key={x} x1={x + 142} y1={y + 36} x2={nextX - 14} y2={nextY + 36} />;
          })}
        </g>

        {stages.map(([number, label], index) => {
          const [x, y] = positions[index];
          const words = label.split(" ");
          const midpoint = Math.ceil(words.length / 2);
          const lines = label.length > 19 ? [words.slice(0, midpoint).join(" "), words.slice(midpoint).join(" ")] : [label];
          return (
            <g className="pipeline-node" key={number} transform={`translate(${x} ${y})`}>
              <rect width="148" height="72" />
              <text className="pipeline-number" x="12" y="17">{number}</text>
              {lines.map((line, lineIndex) => (
                <text className="pipeline-label" key={line} x="74" y={lines.length === 1 ? 43 : 34 + lineIndex * 20} textAnchor="middle">
                  {line}
                </text>
              ))}
            </g>
          );
        })}

        <g className="reject-route">
          <path d="M897 270 C900 327 845 344 815 383" />
          <circle cx="815" cy="383" r="4" />
          <text x="794" y="414" textAnchor="middle">REJECTED</text>
          <text x="794" y="435" textAnchor="middle">does not enter training</text>
        </g>
        <g aria-hidden="true">
          {routeSegments.map((segment, index) => {
            const timing = signalTiming(index);
            return (
              <circle
                className="pipeline-signal"
                cx={segment.x1}
                cy={segment.y1}
                key={`${segment.x1}-${segment.y1}`}
                opacity="0"
                r="4"
              >
                <animate
                  attributeName="cx"
                  calcMode="spline"
                  dur={animationDuration}
                  keySplines="0 0 1 1;0.42 0 1 1;0 0 1 1"
                  keyTimes={timing.movement}
                  repeatCount="indefinite"
                  values={`${segment.x1};${segment.x1};${segment.x2};${segment.x2}`}
                />
                <animate
                  attributeName="cy"
                  calcMode="spline"
                  dur={animationDuration}
                  keySplines="0 0 1 1;0.42 0 1 1;0 0 1 1"
                  keyTimes={timing.movement}
                  repeatCount="indefinite"
                  values={`${segment.y1};${segment.y1};${segment.y2};${segment.y2}`}
                />
                <animate
                  attributeName="opacity"
                  dur={animationDuration}
                  keyTimes={timing.opacity}
                  repeatCount="indefinite"
                  values="0;0;1;1;0;0"
                />
              </circle>
            );
          })}
        </g>
      </svg>

      <ol className="pipeline-mobile" aria-label="MAVERICK pipeline stages">
        {stages.map(([number, label]) => (
          <li key={number}>
            <span>{number}</span>
            <strong>{label}</strong>
          </li>
        ))}
        <li className="pipeline-mobile-reject">
          <span>↳</span>
          <strong>Rejected examples do not enter training</strong>
        </li>
      </ol>
    </div>
  );
}
